// Loading-state skeletons for the live read pages (RFC-0009 Task 1.3, §6).
//
// Each skeleton mirrors the final layout of the surface it stands in for so the
// page does not reflow when data arrives. Containers carry `role="status"` +
// an accessible label so component tests can assert the loading state.

interface SkeletonProps {
  /** Extra classes for sizing the shimmer block. */
  className?: string;
}

/** A single shimmering placeholder block. */
export function Skeleton({ className = '' }: SkeletonProps) {
  return <div aria-hidden="true" className={`animate-pulse rounded bg-accent-faint ${className}`} />;
}

interface CountProps {
  count?: number;
}

/** Placeholder for a row of summary stat tiles. */
export function StatTilesSkeleton({ count = 4 }: CountProps) {
  return (
    <div
      role="status"
      aria-label="Loading summary"
      data-testid="stat-tiles-skeleton"
      className="grid grid-cols-2 gap-4 lg:grid-cols-4"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-border bg-surface p-5 shadow-[var(--c-shadow-sm)]"
        >
          <Skeleton className="mb-4 h-3 w-24" />
          <Skeleton className="h-7 w-12" />
        </div>
      ))}
    </div>
  );
}

/** Placeholder for a grid of project cards. */
export function CardGridSkeleton({ count = 6 }: CountProps) {
  return (
    <div
      role="status"
      aria-label="Loading repositories"
      data-testid="card-grid-skeleton"
      className="grid grid-cols-1 gap-4 md:grid-cols-2"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-border bg-surface p-5 shadow-[var(--c-shadow-sm)]"
        >
          <div className="flex items-center gap-2.5">
            <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
            <div className="min-w-0 flex-1">
              <Skeleton className="mb-2 h-4 w-40" />
              <Skeleton className="h-3 w-28" />
            </div>
          </div>
          <Skeleton className="mt-4 h-3 w-full" />
          <Skeleton className="mt-2 h-3 w-2/3" />
          <div className="mt-4 flex gap-2">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface ListRowsSkeletonProps extends CountProps {
  rows?: number;
}

/** Placeholder for a table/list of rows (used by the sync-issues view). */
export function ListRowsSkeleton({ rows = 6 }: ListRowsSkeletonProps) {
  return (
    <div
      role="status"
      aria-label="Loading list"
      data-testid="list-rows-skeleton"
      className="divide-y divide-border rounded-xl border border-border bg-surface"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}
